import React from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListOl, faTruck } from "@fortawesome/free-solid-svg-icons";

import { PRIORITY_LEVELS } from "admin/assignment/lib/constants";
import { basicOptions } from "../../task-list/lib/constants";
import { Select } from "../../form-inputs/plain-inputs";
import { WithIcon, WithTooltip } from "../../../shared/table-helpers";
import { SHIPMENT_FILTER_TYPES, SHIPMENT_TYPE_MAP } from "../../shipments/lib/constants";
import { inputFilter } from "../../task-list/lib/constants";
import { DueDateFilterButton } from "../../table/components/dueDateFilterButton";
import { getFormattedDuration } from "../../../shared/calculate-duration";
import AssignmentCell from "components/task-list/components/task-cells/assignment-cell";
import { convertToTimeZone } from "date-fns-timezone";
import { format } from "date-fns";
import { DATE_FNS_SHORT_DATE_FORMAT } from "../../../shared/constants";
import { TaskStatus } from "../../../shared/constants/task-status";

const STATUS_OPTIONS = [
    { text: "Ready", value: TaskStatus.READY },
    { text: "Open", value: TaskStatus.OPEN },
    { text: "Active", value: TaskStatus.ACTIVE },
    { text: "Partial Ready", value: TaskStatus.PARTIAL_READY },
    { text: "Redo", value: TaskStatus.REDO },
];

export const DEFAULT_SORT = [
    {
        id: "isRush",
        desc: true,
    },
];

export const DEFAULT_FILTER = [
    {
        id: "shipmentProfilingStateId",
        value: "ready",
    },
];

export const DEFAULT_COLUMNS = {
    priority: { accessor: "profilingTasksAssignmentPriority", label: "Priority", value: true },
    upcs: { accessor: "upcs", label: "# of UPCs", value: true },
    isRush: { accessor: "isRush", label: "Rush", value: true },
    locationDataSource: { accessor: "locationDataSource", label: "Source", value: true },
    schedulingDueDateTime: { accessor: "schedulingDueDateTime", label: "Due Date (Central Time)", value: true },
    duration: { accessor: "", label: "Duration", value: true },
    receiver: { accessor: "receivedBy.name", label: "Receiver", value: true },
    carrier: { accessor: "carrier.name", label: "Carrier", value: true },
    client: { accessor: "client.name", label: "Client", value: false },
    cesClient: { accessor: "cxHClient.name", label: "CES Client", value: true },
    supplier: { accessor: "supplier.name", label: "Supplier", value: true },
    shipmentType: { accessor: "shipmentType", label: "Shipment Type", value: true },
    shipmentProfilingStateId: { accessor: "shipmentProfilingStateId", label: "Status", value: true },
    teamAssignment: { accessor: "profilingTasksAssignmentGroupKey", label: "Team Assignment", value: true },
    userAssignment: { accessor: "profilingTasksAssignedUserInfo", label: "User Assignment", value: true },
    lastOpenedDateTime: { accessor: "lastOpenedDateTime", label: "Last Opened", value: true },
};

export const COLUMNS = (statusTypes, userColumns, locationDataSources, assignments, userAssignments, onUpdateUserAssignments, updateTeamFilter) => {
    return Object.keys(userColumns).reduce((selectedColumns, key) => {
        if (!userColumns[key].value) {
            return selectedColumns;
        }

        switch (key) {
            case "priority":
                selectedColumns.push({
                    Header: WithIcon("Priority", faListOl),
                    accessor: "profilingTasksAssignmentPriority",
                    Cell: (row) => {
                        return row.value && row.value !== 255 ? row.value : "";
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                value={filter ? filter.value : ""}
                                options={[{ text: " ", value: "" }, ...PRIORITY_LEVELS, { text: "≠", value: "null" }]}
                                excludeSelect={true}
                            />
                        );
                    },
                    width: 68,
                    style: { textAlign: "center" },
                });

                break;
            case "upcs":
                selectedColumns.push({
                    Header: WithTooltip("# of UPCs"),
                    filterable: false,
                    sortable: false,
                    accessor: "upcs",
                    Cell: (row) => {
                        return WithTooltip(row.value.length);
                    },
                    width: 68,
                    style: { textAlign: "center" },
                });

                break;
            case "isRush":
                selectedColumns.push({
                    Header: WithTooltip("Rush"),
                    accessor: "isRush",
                    Cell: (row) => {
                        return row.value ? <FontAwesomeIcon icon={faTruck} /> : "";
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                value={filter ? filter.value : "all"}
                            >
                                <option value="all">Show All</option>
                                <option value="true">Is Rush</option>
                                <option value="false">Not Rush</option>
                            </select>
                        );
                    },
                });
                break;
            case "locationDataSource":
                selectedColumns.push({
                    Header: WithTooltip("Source"),
                    accessor: "locationDataSource",
                    Cell: (row) => {
                        return WithTooltip(row.value);
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                excludeSelect={false}
                                value={(filter && filter.value) || ""}
                                options={locationDataSources}
                            />
                        );
                    },
                });

                break;
            case "schedulingDueDateTime":
                selectedColumns.push({
                    Header: WithTooltip("Due Date (Central Time)"),
                    accessor: "schedulingDueDateTime",
                    Filter: ({ filter, onChange }) => {
                        return (
                            <DueDateFilterButton
                                filterProp={filter}
                                onChangeProp={onChange}
                            />
                        );
                    },
                    Cell: (row) => {
                        const date = convertToTimeZone(row.value, {
                            timeZone: "America/Chicago",
                        });

                        return WithTooltip(row.value ? format(date, DATE_FNS_SHORT_DATE_FORMAT) : "");
                    },
                });
                break;
            case "duration":
                selectedColumns.push({
                    Header: WithTooltip("Duration"),
                    accessor: "",
                    Cell: (row) => {
                        return WithTooltip(getFormattedDuration(row.original.schedulingDueDateTime));
                    },
                    filterable: false,
                    sortable: false,
                });
                break;
            case "receiver":
                selectedColumns.push({
                    Header: WithTooltip("Receiver"),
                    accessor: "receivedBy.name",
                    Filter: inputFilter,
                    Cell: (row) => {
                        return WithTooltip(row.value);
                    },
                });
                break;
            case "carrier":
                selectedColumns.push({
                    Header: WithTooltip("Carrier"),
                    accessor: "carrier.name",
                    Filter: inputFilter,
                    Cell: (row) => {
                        return WithTooltip(row.value);
                    },
                });
                break;
            case "client":
                selectedColumns.push({
                    Header: WithTooltip("Client"),
                    accessor: "client.name",
                    Filter: inputFilter,
                    Cell: (row) => {
                        return WithTooltip(row.value);
                    },
                });
                break;
            case "cesClient":
                selectedColumns.push({
                    Header: WithTooltip("CES Client"),
                    accessor: "cxHClient.name",
                    Filter: inputFilter,
                    Cell: (row) => {
                        return WithTooltip(row.value);
                    },
                });
                break;
            case "supplier":
                selectedColumns.push({
                    Header: WithTooltip("Supplier"),
                    accessor: "supplier.name",
                    Filter: inputFilter,
                    Cell: (row) => {
                        return WithTooltip(row.value);
                    },
                });
                break;
            case "shipmentType":
                selectedColumns.push({
                    Header: WithTooltip("Shipment Type"),
                    accessor: "shipmentType",
                    Cell: (row) => {
                        const type = SHIPMENT_TYPE_MAP[row.original.shipmentType].label;

                        return WithTooltip(type);
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                value={(filter && filter.value) || "active"}
                                id="select-type-filter"
                                options={SHIPMENT_FILTER_TYPES}
                                excludeSelect={true}
                            />
                        );
                    },
                });
                break;
            case "shipmentProfilingStateId":
                selectedColumns.push({
                    Header: WithTooltip("Status"),
                    accessor: "shipmentProfilingStateId",
                    Cell: (row) => {
                        if (statusTypes && statusTypes.applicableStates) {
                            const status = statusTypes.applicableStates.filter((value) => value.stateId === row.value)[0];

                            return WithTooltip(status?.description);
                        }
                    },
                    filterMethod: (filter, row) => {
                        if (filter.value && filter.value !== "active" && statusTypes && statusTypes.applicableStates) {
                            const rowStatus = statusTypes.applicableStates.filter((value) => row[filter.id] === value.stateId)[0];

                            if (!rowStatus) {
                                return false;
                            }

                            if (rowStatus.stateId === 1) {
                                rowStatus.state = "open";
                            }

                            return rowStatus.state === filter.value;
                        } else {
                            return true;
                        }
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                value={(filter && filter.value) || "active"}
                                id="select-status-filter"
                                options={STATUS_OPTIONS}
                                excludeSelect={true}
                            />
                        );
                    },
                });
                break;
            case "teamAssignment":
                selectedColumns.push({
                    Header: WithTooltip("Team Assignment"),
                    accessor: "profilingTasksAssignmentGroupKey",
                    filterable: true,
                    Cell: (row) => {
                        return (
                            <AssignmentCell
                                options={assignments}
                                row={row}
                                isProfiling
                                assignedUser={row.original.profilingTasksAssignedUserInfo?.userId}
                            />
                        );
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => {
                                    updateTeamFilter(event.target.value);
                                    onChange(event.target.value);
                                }}
                                style={{ width: "100%" }}
                                value={(filter && filter.value) || ""}
                                excludeSelect={true}
                                id="select-all-filter"
                                options={[...basicOptions, ...assignments]}
                            />
                        );
                    },
                });
                break;
            case "userAssignment":
                selectedColumns.push({
                    Header: WithTooltip("User Assignment"),
                    accessor: "profilingTasksAssignedUserInfo",
                    id: "userId",
                    filterable: true,
                    Cell: (row) => {
                        const displayName = row.value ? row.value.fullName : "";

                        return <span>{displayName}</span>;
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => {
                                    onUpdateUserAssignments(event.target.value);
                                    onChange(event.target.value);
                                }}
                                style={{ width: "100%" }}
                                value={(filter && filter.value) || ""}
                                excludeSelect={true}
                                id="select-all-filter"
                                options={[...basicOptions, ...userAssignments]}
                            />
                        );
                    },
                });
                break;
            case "lastOpenedDateTime":
                selectedColumns.push({
                    Header: WithTooltip("Last Opened"),
                    accessor: "lastOpenedDateTime",
                    Cell: (row) => {
                        const lastOpened = moment.utc(row.value).fromNow();
                        const lastOpenedBy = row.original.lastOpenedBy ? row.original.lastOpenedBy.split("|").pop() : "";
                        const value = row.value ? `${lastOpened} - ${lastOpenedBy}` : "";

                        return WithTooltip(value);
                    },
                    filterable: false,
                });
                break;
            default:
                return selectedColumns;
        }

        return selectedColumns;
    }, []);
};
